import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { TSavedSearch } from "#next/components/catalog/base/shared";

export const SortFields = ["Name", "Last loaded"] as const;

export interface CatalogUIState {
  originalSearchResults: TSavedSearch[];
  originalSearchResultsHash: string;
  filteredSearchResults: TSavedSearch[];
  sortBy: (typeof SortFields)[number] | null;
  sortDirection: string | null;
  nameFilter: string | null;
  selectedTab: number;
  previewSearchId: string | null;
  userIsPreviewing: boolean;
  isLoaded: boolean; // We load some initial default results when loading, so the TopControlBar can be displayed. During this time, isLoaded=false.
}

const initialCatalogUiState = (
  searchResults: TSavedSearch[],
  searchResultsHash: string,
  isLoaded: boolean,
): CatalogUIState => {
  return {
    originalSearchResults: searchResults,
    originalSearchResultsHash: searchResultsHash,
    filteredSearchResults: searchResults,
    nameFilter: null,
    sortBy: "Name",
    sortDirection: "asc",
    selectedTab: 0,
    previewSearchId: null,
    userIsPreviewing: false,
    isLoaded,
  };
};

type CatalogState = Record<string, CatalogUIState>;

const initialState = (): CatalogState => ({});

const getUiStateOrThrow = (state: CatalogState, searchResultsKey: string) => {
  const uiState = state[searchResultsKey];
  if (!uiState) {
    throw new Error(`No state found for key ${searchResultsKey}`);
  }
  return uiState;
};

const _filterName = (searches: TSavedSearch[], nameFilter: string) => {
  return searches.filter((search) =>
    search.name.toLowerCase().includes(nameFilter.toLowerCase()),
  );
};

const _filterTab = (searches: TSavedSearch[], selectedTab: number) => {
  if (selectedTab === 1)
    return searches.filter((search) => search.editable === true);
  if (selectedTab === 2)
    return searches.filter((search) => search.editable === false);
  return searches;
};

const updateFilteredSearchResults = (
  state: CatalogState,
  searchResultsKey: string,
) => {
  const uiState = getUiStateOrThrow(state, searchResultsKey);

  let filteredSearchResults = uiState.originalSearchResults;

  if (uiState.nameFilter) {
    filteredSearchResults = _filterName(
      filteredSearchResults,
      uiState.nameFilter,
    );
  }

  if (uiState.selectedTab) {
    filteredSearchResults = _filterTab(
      filteredSearchResults,
      uiState.selectedTab,
    );
  }

  function sort(a: TSavedSearch, b: TSavedSearch) {
    const left = uiState.sortDirection === "desc" ? a : b;
    const right = uiState.sortDirection === "desc" ? b : a;
    if (!uiState.sortBy || uiState.sortBy === "Last loaded") {
      if (left.lastExecutedAt == null) {
        return -1;
      }
      if (right.lastExecutedAt == null) {
        return 1;
      }
      return (
        new Date(left.lastExecutedAt).getTime() -
        new Date(right.lastExecutedAt).getTime()
      );
    } else if (uiState.sortBy === "Name") {
      // the conditional statements are required to move null names to the bottom when sorting
      if (left.name === right.name) {
        return 0;
      }
      if (left.name === null) {
        return -1;
      }
      if (right.name === null) {
        return 1;
      }
      return right.name.localeCompare(left.name);
    }
    throw new Error(`Impossible sort ${uiState.sortBy}`);
  }

  uiState.filteredSearchResults = filteredSearchResults.toSorted(sort);
};

const catalogSlice = createSlice({
  name: "catalog",
  initialState,
  reducers: {
    setNameFilter: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        nameFilter: string | null;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.nameFilter = action.payload.nameFilter;
      updateFilteredSearchResults(state, action.payload.searchResultsKey);
    },
    setSelectedTab: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        selectedTab: number;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.selectedTab = action.payload.selectedTab;
      updateFilteredSearchResults(state, action.payload.searchResultsKey);
    },
    setSortBy: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        sortBy: (typeof SortFields)[number] | null;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.sortBy = action.payload.sortBy;
      updateFilteredSearchResults(state, action.payload.searchResultsKey);
    },
    setSortDirection: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        direction: "asc" | "desc" | null;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.sortDirection = action.payload.direction;
      updateFilteredSearchResults(state, action.payload.searchResultsKey);
    },
    setPreviewState: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        previewSearchId: string | null;
        userIsPreviewing: boolean;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.previewSearchId = action.payload.previewSearchId;
      uiState.userIsPreviewing = action.payload.userIsPreviewing;
    },
    setOriginalSearchResults: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        searchResultsHash: string;
        searchResults: TSavedSearch[];
        isLoaded: boolean;
      }>,
    ) => {
      const { searchResultsKey, searchResultsHash, searchResults, isLoaded } =
        action.payload;

      state[searchResultsKey] = initialCatalogUiState(
        searchResults,
        searchResultsHash,
        isLoaded,
      );

      updateFilteredSearchResults(state, searchResultsKey);
    },
  },
});

export const {
  setNameFilter,
  setSelectedTab,
  setSortBy,
  setSortDirection,
  setPreviewState,
  setOriginalSearchResults,
} = catalogSlice.actions;

export const reducer = catalogSlice.reducer;

export const selectNameFilter = (state: CatalogUIState) => state.nameFilter;

export const selectSortBy = (state: CatalogUIState) => state.sortBy;

export const selectSortDirection = (state: CatalogUIState) =>
  state.sortDirection ?? "desc";

export const selectSelectedTab = (state: CatalogUIState) => state.selectedTab;

export const selectPreviewState = (state: CatalogUIState) => ({
  searchId: state.previewSearchId,
  userIsPreviewing: state.userIsPreviewing,
});
